import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BhvrUser } from '../../authentication/Authentication.model';
import { useAuth } from '../../authentication/AuthenticationContext';
import { useConfig } from '../../ConfigContext';
import { useSnackbar } from '../../snackbar/SnackbarContext';
import { get, patch } from '../../utils/Request';
import { EditButton } from '../../_shared/edit-button/EditButton';
import { CheckboxInput } from '../../_shared/form/CheckboxInput';
import { PageLayout } from '../layout/PageLayout';
import { MarketingPlayerSubscriptions } from '../my-games/MyGames.model';

type IterableValues = {
  [listId: `${string}-${string}`]: boolean;
};

type FormValues = IterableValues & {
  communicationLang?: string;
};

type Props = {
  noAuth?: boolean,
};

interface PlayerMarketingPreferences {
  communcationLang: string;
  subscriptionsPerProjects: MarketingPlayerSubscriptions[];
}

export function CommunicationPreferences({ noAuth }: Props): JSX.Element {
  const { t } = useTranslation();
  const formikRef = useRef<FormikProps<FormValues>>();
  const config = useConfig();
  const snackbar = useSnackbar();
  const auth = useAuth();
  const marketingProjects = [
    { name: 'bhvraccount', marketingConfig: config.serverConfig.bhvrAccountConfig?.iterable },
    ...config
      .serverConfig
      .gamesConfig
      .map((gc) => ({ name: gc.id, marketingConfig: gc.marketingConfig?.iterable }))]
    .filter((c) => c.marketingConfig && Object.keys(c.marketingConfig).length);
  const [initialValuesFormik, setInitialValuesFormik] = useState<FormValues>({
    communicationLang: '',
  });
  const [editEnabled, setEditEnabled] = useState<boolean>(noAuth);

  const hasSubscriptionsChanged = (initial: FormValues, current: FormValues): boolean => {
    const changedFields = Object.keys(current).filter((key) => {
      if (key === 'communicationLang') {
        return false;
      }
      const iterableKey = key as `${string}-${string}`;
      return initial[iterableKey] !== current[iterableKey];
    });
    return changedFields.length > 0;
  };

  const handleSubmitForm = async (values: FormValues): Promise<void> => {
    const hasSubChanged = hasSubscriptionsChanged(initialValuesFormik, values);
    if (hasSubChanged || initialValuesFormik?.communicationLang !== values.communicationLang) {
      try {
        if (initialValuesFormik?.communicationLang !== values.communicationLang) {
          const response = await patch<BhvrUser>(`${config.clientConfig?.host}/players/me`, {
            data: {
              communicationLang: values.communicationLang,
            },
          });
          auth.updateUser(response);
        }

        if (hasSubChanged) {
          const subscriptionsPerProjects = marketingProjects.map((project) => {
            const subscribedMessages: string[] = [];
            for (const mt of project.marketingConfig.messageTypes) {
              if (values[`${project.name}-${mt.name}`]) {
                subscribedMessages.push(mt.name);
              }
            }
            return { project: project.name, subscribedMessages };
          });
          await patch<PlayerMarketingPreferences>(`${config.clientConfig?.host}/players/me/marketing/subscriptions`, {
            data: {
              subscriptionsPerProjects,
            },
          });
        }

        snackbar.addSuccessMessage('account.settings.success');
        setEditEnabled(false);
        setInitialValuesFormik(values);
      } catch (e) {
        snackbar.addErrorMessage('UnknownError');
      }
    }
  };

  const toggleCheck = (setFieldValue: (field: string, value: unknown) => void, uncheck: boolean): void => {
    for (const project of marketingProjects) {
      for (const list of project.marketingConfig.messageTypes) {
        setFieldValue(`${project.name}-${list.name}`, !uncheck);
      }
    }
  };

  const toggleEdit = (): void => {
    if (editEnabled) {
      setEditEnabled(false);
      formikRef.current?.resetForm();
    } else {
      setEditEnabled(true);
    }
  };

  useEffect(() => {
    if (auth?.user) {
      setInitialValuesFormik((oldVals) => ({
        ...oldVals,
        communicationLang: auth.user.communicationLang,
      }));
    }
  }, [auth.user]);

  useEffect(() => {
    get<MarketingPlayerSubscriptions[]>(`${config.clientConfig?.host}/players/me/marketing/subscriptions`)
      .then((playerSubscriptions) => {
        const iterableInitialValues: IterableValues = {};
        for (const playerSubscription of playerSubscriptions) {
          for (const subscribedMessage of playerSubscription.subscribedMessages) {
            iterableInitialValues[`${playerSubscription.project}-${subscribedMessage}`] = true;
          }
        }
        setInitialValuesFormik((oldVals) => ({
          ...oldVals,
          ...iterableInitialValues,
        }));
      });
  }, []);

  return (
    <PageLayout hideMenu={noAuth}>
      <div className='communication-preferences-container'>
        <div className='account-data-container'>
          <h2>{t('account.settings.communication.title')}</h2>
          <p className='settings-subtitle'>{t('account.settings.communication.description')}</p>

          <div className='form-container'>
            {!noAuth && (
              <div className='form-container-header justify-content-end'>
                <EditButton
                  isActive={editEnabled}
                  onClick={toggleEdit}
                  aria-label='Edit Communication Preferences'
                  data-cy='edit-communication-preferences-button'
                />
              </div>
            )}
            <Formik
              initialValues={initialValuesFormik}
              innerRef={formikRef}
              enableReinitialize
              onSubmit={handleSubmitForm}
            >
              {({ isSubmitting, handleReset, values, setFieldValue }) => (
                <Form className='form-light'>
                  {!noAuth && (
                    <Field name='communicationLang'>
                      {({ field }: FieldProps) => (
                        <div className='form-group'>
                          <label htmlFor='communicationLang'>{t('account.settings.communication.preferredLang')}</label>
                          <select
                            id='communicationLang'
                            data-cy='language-preference-form'
                            className='form-control'
                            disabled={!editEnabled || isSubmitting}
                            {...field}
                          >
                            <option value='en' key='en'>
                              {t('countries.iso-to-name.en')}
                            </option>

                            <option value='fr' key='fr'>
                              {t('countries.iso-to-name.fr')}
                            </option>

                            <option value='ja' key='ja'>
                              {t('countries.iso-to-name.ja')}
                            </option>
                          </select>
                        </div>
                      )}
                    </Field>
                  )}

                  {marketingProjects.map((item) => (
                    <div key={item.name} className='mt-5'>
                      <h4 className='mb-3'>{t(`account.settings.communication.mailingLists.${item.name}.name`)}</h4>
                      <div className='ml-3'>
                        {item.marketingConfig.messageTypes.map((list) => (
                          <CheckboxInput
                            key={list.name}
                            name={`${item.name}-${list.name}`}
                            title={
                              t(`account.settings.communication.mailingLists.${item.name}.lists.${list.name}.name`)
                            }
                            label={
                              t(`account.settings.communication.mailingLists.${item.name}.lists.${list.name}.text`)
                            }
                            disabled={!editEnabled || isSubmitting}
                            containerClassName=''
                          />
                        ))}
                      </div>
                    </div>
                  ))}

                  {editEnabled && (
                    <>
                      <div className='form-group mt-2 mb-0 d-flex'>
                        <button
                          type='button'
                          className='btn btn-link'
                          onClick={() => toggleCheck(setFieldValue, Object.values(values).includes(true))}
                        >
                          {Object.values(values).includes(true) ? 'Uncheck All' : 'Check All'}
                        </button>
                      </div>
                      {!noAuth && (
                        <div className='form-group mt-5 d-flex justify-content-between'>
                          <button
                            type='button'
                            className='btn btn-link'
                            onClick={() => { handleReset(); toggleEdit(); }}
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            type='submit'
                            data-cy='submit-button'
                            className='btn btn-primary-game'
                            disabled={isSubmitting}
                          >
                            {t('button.save')}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
